import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  Routes as RouterRoutes,
  Route,
  useLocation
} from 'react-router-dom'
import { Amplify } from 'aws-amplify'
import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'
import Navbar from './components/Navbar'
import ModalBody from './components/ModalBody'
import LandingPage from './pages/LandingPage'
import { device, spacing } from './styles/_var'
import Signup from './pages/Signup'
import Login from './pages/Login'
import { routes } from './utils/constants'
import DashboardRoutes from './pages/DashboardRoutes'
import Categories from './pages/Categories'
import Competitions from './pages/Competitions'
import CompetitionPage from './pages/CompetitionPage/CompetitionPage'
import LoadingScreen from './components/LoadingScreen'
import { observer } from 'mobx-react-lite'
import uiStore from './stores/uiStore'
import CompetitionDashboardRoutes from './pages/CompetitionDashboardRoutes'
import authStore from './stores/authStore'
import Auth from './utils/auth'
import Modal from './components/Modal'
import Logout from './pages/Logout'
import ConfirmDialogBody from './components/ConfirmDialogBody'
import competitionStore from './stores/competitionStore'
import ErrorPage from './pages/ErrorPages/ErrorPage'
import Toast from './components/Toast'
import AccountVerification from './pages/AccountVerification'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import ContactUs from './pages/ContactUs'
import Pricing from './pages/Pricing'
import paymentStore from './stores/paymentStore'
import CreditCardSelector from './components/CreditCardSelector'
import ComingSoon from './pages/ComingSoon'
import Faqs from './pages/FAQS/Faqs'
import Features from './pages/Features/index'
import Demos from './pages/Demos'
import PrivacyPolicy from './pages/PrivacyPolicy'
import { useSubdomain } from './hooks/useSubdomain'

const PaystackButton = require('react-paystack').PaystackButton

const runAppPreflight = () => {
  if (isEmpty(authStore.userData)) {
    const userDetails = Auth.fetchUserDetails()
    if (!isEmpty(userDetails)) {
      authStore.setUserDetails(userDetails)
    }

    if (Auth.isLoggedIn()) {
      authStore.fetchUserDetails()
    }
  }

  competitionStore.fetchCompetitionCategories()
  competitionStore.fetchSubscriptionOptions()
  Amplify.configure({
    "API": {
      "Events": {
        "endpoint": process.env.REACT_APP_EVENTS_ENDPOINT || '',
        "region": process.env.REACT_APP_EVENTS_REGION || '',
        "defaultAuthMode": "apiKey",
        "apiKey": process.env.REACT_APP_EVENTS_API_KEY || ''
      }
    }
  })
}

const NonAuthRoutes = ({ useSubdomainRoutes }: { useSubdomainRoutes?: boolean }) => {
  return (
    <>
      <Navbar />
      <RouterRoutes>
        {!useSubdomainRoutes && <Route path={routes.LANDING} element={<LandingPage />} />}
        <Route path={routes.CATEGORIES} element={<Categories />} />
        <Route path={routes.COMPETITIONS} element={<Competitions />} />
        <Route path={routes.SIGNUP} element={<StyledSignup />} />
        <Route path={routes.LOGIN} element={<StyledLogin />} />
        <Route path={routes.LOGOUT} element={<Logout />} />
        <Route path={routes.ERROR_PAGE} element={<ErrorPage />} />
        <Route path={routes.ACCOUNT_VERIFICATION} element={<AccountVerification />} />
        <Route path={routes.FORGOT_PASSWORD} element={<StyledForgotPassword />} />
        <Route path={routes.RESET_PASSWORD} element={<StyledResetPassword />} />
        <Route path={routes.CONTACT_US} element={<ContactUs />} />
        <Route path={routes.PRICING} element={<Pricing />} />
        <Route path={routes.COMING_SOON} element={<ComingSoon />} />
        <Route path={routes.FAQS} element={<Faqs />} />
        <Route path={routes.FEATURES} element={<Features />} />
        <Route path={routes.DEMOS} element={<Demos />} />
        <Route path={routes.PRIVACY_POLICY} element={<PrivacyPolicy />} />
        {useSubdomainRoutes ? <Route path='*' element={<CompetitionPage />} /> : <Route path='/:competition_id/*' element={<CompetitionPage />} />}
      </RouterRoutes>
    </>
  )
}

const RegularRoutes = ({ useSubdomainRoutes }: { useSubdomainRoutes?: boolean }) => {
  return (
    <RouterRoutes>
      <Route path='/:competition_username/dashboard/*' element={<CompetitionDashboardRoutes />} />
      <Route path='/dashboard/*' element={<DashboardRoutes />} />
      <Route path='/*' element={<NonAuthRoutes useSubdomainRoutes={useSubdomainRoutes} />} />
    </RouterRoutes>
  )
}

const Routes: FunctionComponent = () => {
  const location = useLocation()
  const subdomain = useSubdomain()
  const useSubdomainRoutes = !!subdomain
  
  useEffect(() => {
    window.scroll(0, 0)
  }, [location])

  const [loadApp, setLoadApp] = useState(false)
  const { displayLoadingScreen, loadingScreenMessage, showModal, modalPayload } = uiStore

  useEffect(() => {
    runAppPreflight()
    setLoadApp(true)
  }, [])

  if (!loadApp) return null

  return (
    <>
      {!!uiStore.toastMessage && <Toast />}
      <Modal style={{zIndex: 6 }} onClose={() => uiStore.closeModal()} showModal={showModal}>
        <ModalBody {...modalPayload} />
      </Modal>
      <Modal
        onClose={() => uiStore.closeConfirmDialog()}
        showModal={uiStore.showConfirmDialog}
      >
        <ConfirmDialogBody
          message={uiStore.confirmMessage}
          onClose={() => uiStore.closeConfirmDialog()}
          denyAction={() => uiStore.denyAction && uiStore.denyAction()}
          confirmAction={() => uiStore.confirmAction && uiStore.confirmAction()} />
      </Modal>
      <CreditCardSelector
        onCardSelected={(card) => paymentStore.proceedPaymentWithCard(card)}
        triggerNewPayment={() => paymentStore.triggerNewCardPayment()}
        creditCards={paymentStore.savedCards}
        show={paymentStore.showCreditCardSelector}
        close={() => paymentStore.closeCreditCardSelector()}
      />
      {(paymentStore.showPaystackModal && paymentStore.hasActivePayment) && (
        <PaystackButtonWrapper>
          <PaystackButton
            {...paymentStore.paymentPayload}
            className="paystack-btn"
            onClose={() => paymentStore.setShowPaystackModal(false)}
            onSuccess={(data: any) => paymentStore.onPaystackSuccessful(data)} />
        </PaystackButtonWrapper>
      )}
      {displayLoadingScreen && <LoadingScreen message={loadingScreenMessage} />}
      <RegularRoutes useSubdomainRoutes={useSubdomainRoutes} />
    </>
  )
}

export default observer(Routes)

const AuthStyle = `
  margin: 0 auto;
  position: relative;
  top: 50%;
  transform: translateY(-55%);

  @media ${device.laptop} {
    position: absolute;
    right: ${spacing.xxl};
  } 
`

const StyledSignup = styled(({ ...rest }) => <Signup {...rest} />)`${AuthStyle}`

const StyledLogin = styled(({ ...rest }) => <Login {...rest} />)`${AuthStyle}`

const StyledForgotPassword = styled(({ ...rest }) => <ForgotPassword {...rest} />)`${AuthStyle}`

const StyledResetPassword = styled(({ ...rest }) => <ResetPassword {...rest} />)`${AuthStyle}`

const PaystackButtonWrapper = styled.div`
  visibility: hidden;
  position: absolute;
`